import { Subject, Observable, BehaviorSubject } from 'rxjs'
import { Injectable } from '@angular/core'
import { ApiCall } from './rest-client.service'
import { ViewModel } from '../view-model'
import { AuthorizationService, TicketManager } from '@ar2/common'
import { Store } from '@ngrx/store'

/**
 * 画面上で発火するイベントオブジェクト
 * @author nabla2.metamodel.generator
 */
export class UIEvent<T extends (Event | ApiResponse)> {
  type     :string
  id       :string
  source   :T
  index?   :number
  content? :string
}

export class ApiResponse {
  apiCall   :ApiCall
  response  :Response
  jsonBody? :any
}

/**
 * イベントハブ
 */
@Injectable()
export class UIEventHub {
  static ENTER_KEY_CODE: number = 13

  private _event$:Subject<UIEvent<any>> = new Subject<UIEvent<any>>()
  private model$:BehaviorSubject<ViewModel> = new BehaviorSubject<ViewModel>(null)

  constructor(
    public store: Store<{viewModel:ViewModel}>,
    private authService: AuthorizationService,
    private ticketMgr: TicketManager,
  ) {
    this.store.select('viewModel').subscribe(v => this.model$.next(v))
    this.click$ = this.event$.filter(e => e.type === 'click').map(e => <UIEvent<Event>>e)
    this.enterKeyUp$ = Observable.fromEvent(document, 'keyup')
                          .startWith(null)
                          .pairwise()
                          .map(([prev, cur]: [KeyboardEvent, KeyboardEvent]) => {
                            return (!prev || !prev['isComposing']) && cur.keyCode == UIEventHub.ENTER_KEY_CODE
                              ? { id: "", source: cur, type: "enter" }
                              : null
                           })
                          .filter(e => !!e)
                          .map(e => <UIEvent<Event>> e)
    this.change$ = this.event$.filter(e => e.type === 'change').map(e => <UIEvent<Event>>e)
    this.pageInitialized$ = this.event$.filter(e => e.type === 'page_initialized').map(e => <UIEvent<Event>>e)
    this.apiResponse$ = this.event$.filter(e => e.type === 'api_response').map(e => <UIEvent<ApiResponse>>e)
    this.scrollEnd$ = this.event$.filter(e => e.type === 'scroll_end').map(e => <UIEvent<Event>>e)
    this.clickChart$ = this.event$.filter(e => e.type === 'click_chart').map(e => <UIEvent<Event>>e)
    this.jsonResponse$ = this.apiResponse$.filter(e => {
      const response = e.source.response
      const status = response.status
      const contentType = response.headers.get("content-type")
      return contentType && contentType.includes('json')
        && (200 <= status && status < 300 && !e.source.apiCall.downloadAsFile || 400 <= status && status < 500)
    })
    .flatMap(e => {
      const response = e.source.response
      const apiCall = e.source.apiCall
      return response.json().then(json => {
        return {
          ...e,
          source: {
            apiCall: apiCall,
            response: response,
            jsonBody: json,
          },
        }
      })
    })
    .share()
  }

  next(event:UIEvent<any>):void {
    this._event$.next(event)
  }

  /**
   * 全てのイベントからなるストリーム
   */
  get event$():Observable<UIEvent<any>> {
    return this._event$
  }

  /**
   * 現在の表示モデルを取得する
   */
  get model():ViewModel {
    return this.model$.getValue()
  }

  /**
   * 選択したGbuCD
   */
  get selectedGbuCD():string {
    return this.ticketMgr.get() ? this.authService.getSelectedGbuCD() || '' : ''
  }

  /**
   * JWTを所持しているかを判定する
   */
  get hasAuthTicket(): boolean {
    return this.ticketMgr.has();
  }

  /**
   * 画面の各ウィジェットをクリックした場合に発火するイベントストリーム
   */
  click$:Observable<UIEvent<Event>>

  /**
   * エンターキーを押した場合に発火するイベントストリーム
   */
  enterKeyUp$:Observable<UIEvent<Event>>

  /**
   * 画面の各ウィジェットの内容を変更した場合に発火するイベントストリーム
   */
  change$:Observable<UIEvent<Event>>

  /**
   * 各画面の初期処理が完了した時点で発火するイベントストリーム
   */
  pageInitialized$:Observable<UIEvent<Event>>

  /**
   * テーブルの最後までにスクロールした時点で発火するイベントストリーム
   */
  scrollEnd$:Observable<UIEvent<Event>>

  /**
   * 画面の棒グラフのをクリックした場合に発火するイベントストリーム
   */
  clickChart$:Observable<UIEvent<Event>>

  /**
   * APIリクエストに対するレスポンスが返された時点で発火するイベントストリーム
   */
  apiResponse$:Observable<UIEvent<ApiResponse>>

  /**
   * APIリクエストに対し、content-type='appliction/json'のレスポンスが返された場合に
   * json bodyのパース処理完了後に発火するイベントストリーム
   */
  jsonResponse$:Observable<UIEvent<ApiResponse>>

  /**
   * 【画面項目名:id:string】を押したとき
   *
   * idに指定されたUI部品がクリックもしくはタップされた場合に発火する
   */
  whenItemPressed = (id:string):Observable<any> => {
    return this.click$.filter(e => e.id === id)
  }
  /**
   * エンターキーを押したとき
   *
   * エンターキーを押したときに発火する
   */
  whenEnterKeyPressed = ():Observable<any> => {
    return this.enterKeyUp$
  }
  /**
   * 【画面項目名:id:string】を変更したとき
   *
   * idに指定されたUI部品の内容を変更したとき(changedイベント)に発火する
   */
  whenItemChanged = (id:string):Observable<any> => {
    return this.change$.filter(e => e.id === id)
  }
  /**
   * 【画面項目名:id:string】の最後までスクロールしたとき
   *
   * idに指定されたUI部品の最後までスクロールしたとき(scrollEndイベント)に発火する
   */
  whenScrollToEnd = (id:string):Observable<any> => {
    return this.scrollEnd$.filter(e => e.id === id)
  }
  /**
   * 画面が表示されたとき
   *
   * 画面が表示されたとき(正確には初期化が完了したとき)に発火する。
   * なお、別画面に遷移後、再度当該画面に戻ってきた場合にも発火する。
   */
  whenPageInitialized = ():Observable<any> => {
    return this.pageInitialized$.take(1)
  }
  /**
   * 【遷移先画面名:pageName:(m:ViewModel) => string】から遷移してきたとき
   *
   * 指定された画面から遷移してきた場合に発火する。
   */
  whenComeFromCertainPage = (pageName:(m:ViewModel) => string):Observable<any> => {
    return this.pageInitialized$.take(1).filter(e => {
      const prevPage = pageName(this.model)
      return e.source['prevPage'] === prevPage
    })
  }
  /**
   * 【API呼び出し:apiCall:ApiCall】がステータスコード2xxで応答したとき
   *
   * 指定されたAPIがステータスコード2xxで応答した場合に発火する。(レスポンスボディの読み込みは未済)
   */
  whenGivenApiRespondedWith2xx = (apiCall:ApiCall):Observable<any> => {
    return this.apiResponse$.filter(e => {
      return e.id ===  apiCall.id
             && e.source.response.ok
    })
  }
  /**
   * 【API呼び出し:apiCall:ApiCall】が正常応答したとき
   *
   * 指定されたAPIがステータスコード2xxで応答し、レスポンスボディがJSONフォーマットでパースできた場合に発火する。
   */
  whenGivenApiRespondedSuccessfully = (apiCall:ApiCall):Observable<any> => {
    return this.jsonResponse$.filter(e => {
      if (!!e && !!apiCall) {
        return e.id ===  apiCall.id
             && e.source.response.ok
      }
    })
  }
  /**
   * APIが正常応答したとき
   *
   * 送信中のAPIがステータスコード2xxで応答し、レスポンスボディがJSONフォーマットでパースできた場合に発火する。
   */
  whenApiRespondedSuccessfully = ():Observable<any> => {
    return this.jsonResponse$.filter(e => {
      return e.source.response.ok
    })
  }
  /**
   * APIがユーザエラーで応答したとき
   *
   * 送信中のAPIがステタースコード400で応答し、レスポンスボディがJSONフォーマットでパースできた場合に発火する。
   */
  whenApiRespondedUserError = ():Observable<any> => {
    return this.jsonResponse$.filter(e => {
      return e.source.response.status === 400 && !e.source.jsonBody.ignoreWarning
    })
  }
  /**
   * APIがユーザワーニングで応答したとき
   *
   * 送信中のAPIがステタースコード400で応答し、レスポンスボディがJSONフォーマットでパースできた場合に発火する。
   */
  whenApiRespondedUserWarning = ():Observable<any> => {
    return this.jsonResponse$.filter(e => {
      return e.source.response.status === 400 && !!e.source.jsonBody.ignoreWarning
    })
  }
  /**
   * 【値:condition:(m:ViewModel) => any】になったとき
   *
   * 指定された値が指定された条件を満たした場合に発火する。
   * 一旦条件を満たさなくなった後、再度条件を満たした場合にも発火する。
   */
  whenStateMeetsCondition = (condition:(m:ViewModel) => any):Observable<any> => {
    return this.model$
        .map(m => !!condition(m))
        .distinctUntilChanged()
        .filter(met => met)
  }
  /**
   * 【値:condition:(m:ViewModel) => any】が変わったとき
   *
   * 指定された値が変化する度に発火する。
   */
  whenStateGetsChanged = (condition:(m:ViewModel) => any):Observable<any> => {
    return this.model$
        .map(m => condition(m))
        .distinctUntilChanged()
        .skip(1)
  }
  /**
   * 確認ダイアログ【値:condition:(m:ViewModel) => string】ボタンを押したとき
   *
   * 確認ダイアログ内の指定したボタンが押下された場合に発火する。
   */
  whenConfirmDialogButtonPressed = (condition:(m:ViewModel) => string):Observable<any> => {
    return this.click$.filter(e => {
      let fired: boolean = false;
      this.model$.forEach(m => {
        if (e.id === condition(m)) {
          fired = true;
        }
      });
      return fired;
    });
  }
  /**
   * 【画面項目名:id:string】の列番号【値:index:(m:ViewModel) => number】番目の横アコーディオンのボタンを押下したとき
   *
   * テーブルの指定した横アコーディオンのボタンをクリックした場合に発火する。
   */
  whenHorizontalAccordionButtonPressed = (id:string, index:(m:ViewModel) => number):Observable<any> => {
    return this.click$.filter(e => e.id && e.id.endsWith("__hAcdn") && e.id.split("__hAcdn")[0] === id && e.index === index(this.model));
  }
  /**
   * 【画面項目名:id:string】の棒グラフを押したとき
   *
   * idに指定されたグラフがクリックもしくはタップされた場合に発火する
   */
  whenGraphPressed = (id:string):Observable<any> => {
    return this.clickChart$.filter(e => e.id === id)
  }

  /**
   * 【画面モデル項目:path:string】の内容が直近のスナップショットと同じなら
   *
   * 指定されたパス上の表示モデルの内容が直近のスナップショットの状態と同じならtrueとなる。
   * スナップショットが存在しない場合はアプリケーション初期状態との比較となる。
   */
  ifStateRemainsSameWithItsLatestSnapshot = (path:string):(event:UIEvent<Event>) => boolean => {
    return event => {
      const model = this.model
      return !ViewModel.updatedFromLatestSnapshot(path, model, this.authService)
    }
  }
  /**
   * 【画面モデル項目:path:string】の内容が直近のセッションストレージ上のスナップショットと同じなら
   *
   * 指定されたパス上の表示モデルの内容が直近のセッションストレージ上のスナップショットの状態と同じならtrueとなる。
   * セッションストレージ上のスナップショットが存在しない場合はアプリケーション初期状態との比較となる。
   */
  ifStateRemainsSameWithItsLatestSessionStorageSnapshot = (path:string):(event:UIEvent<Event>) => boolean => {
    return event => {
      const model = this.model
      return !ViewModel.updatedFromLatestSnapshot(path, model, this.authService, true)
    }
  }
  /**
   * 【画面モデル項目:path:string】の内容が直近のスナップショットと異なれば
   *
   * 指定されたパス上の表示モデルの内容が直近のスナップショットの状態と異なるならtrueとなる。
   * スナップショットが存在しない場合はアプリケーション初期状態との比較となる。
   */
  ifStateDiffersFromItsLatestSnapshot = (path:string):(event:UIEvent<Event>) => boolean => {
    return event => {
      const model = this.model
      return ViewModel.updatedFromLatestSnapshot(path, model, this.authService)
    }
  }
  /**
   * 【画面モデル項目:path:string】の内容が直近のセッションストレージ上のスナップショットと異なれば
   *
   * 指定されたパス上の表示モデルの内容が直近のセッションストレージ上のスナップショットの状態と異なるならtrueとなる。
   * セッションストレージ上のスナップショットが存在しない場合はアプリケーション初期状態との比較となる。
   */
  ifStateDiffersFromItsLatestSessionStorageSnapshot = (path:string):(event:UIEvent<Event>) => boolean => {
    return event => {
      const model = this.model
      return ViewModel.updatedFromLatestSnapshot(path, model, this.authService, true)
    }
  }
  /**
   * 【画面モデル項目:path:string】が【値:value:(m:ViewModel) => any】であれば
   *
   * 指定されたパス上の表示モデルの内容が、指定された値と一致するならtrueとなる。
   */
  ifStateIsSameWith = (path:string, value:(m:ViewModel) => any):(event:UIEvent<Event>) => boolean => {
    return event => {
      const model = this.model
      return ViewModel.get(path, model) == value(model)
    }
  }
  /**
   * 【画面モデル項目:pathOne:string】が【値:valueOne:(m:ViewModel) => any】かつ【画面モデル項目:pathAnother:string】が【値:valueAnother:(m:ViewModel) => any】であれば
   *
   * 指定されたパス上の表示モデルの内容が、指定された値と一致するならtrueとなる。(AND条件)
   */
  ifOneStateIsSameAndAnotherStateIsSameWith = (pathOne:string, valueOne:(m:ViewModel) => any, pathAnother:string, valueAnother:(m:ViewModel) => any):(event:UIEvent<Event>) => boolean => {
    return event => {
      const model = this.model
      return ViewModel.get(pathOne, model) == valueOne(model) && ViewModel.get(pathAnother, model) == valueAnother(model)
    }
  }
  /**
   * 【画面モデル項目:pathOne:string】が【値:valueOne:(m:ViewModel) => any】であれば、かつ【画面モデル項目:pathAnother:string】が【値:valueAnother:(m:ViewModel) => any】でなければ
   *
   * 指定されたパス上の表示モデルの内容が、指定された値と一致するならtrueとなる。(AND条件)
   */
  ifOneStateIsSameAndAnotherStateIsNotSameWith = (pathOne:string, valueOne:(m:ViewModel) => any, pathAnother:string, valueAnother:(m:ViewModel) => any):(event:UIEvent<Event>) => boolean => {
    return event => {
      const model = this.model
      return ViewModel.get(pathOne, model) == valueOne(model) && ViewModel.get(pathAnother, model) !== valueAnother(model)
    }
  }
  /**
   * 【画面モデル項目:path:string】が【値:value:(m:ViewModel) => any】でなければ
   *
   * 指定されたパス上の表示モデルの内容が、指定された値と一致しないならtrueとなる。
   */
  ifStateIsNotSameWith = (path:string, value:(m:ViewModel) => any):(event:UIEvent<Event>) => boolean => {
    return event => {
      const model = this.model
      return ViewModel.get(path, model) !== value(model)
    }
  }
  /**
   * 【画面モデル項目:path:string】が空であれば
   *
   * 指定されたパス上の表示モデルの内容が、空(=falsy)である場合にtrueとなる。
   */
  ifStateIsBlank = (path:string):(event:UIEvent<Event>) => boolean => {
    return event => {
      const model = this.model
      return !ViewModel.get(path, model)
    }
  }
  /**
   * 【画面モデル項目:path:string】が空でなければ
   *
   * 指定されたパス上の表示モデルの内容が、空でない(=truthy)である場合にtrueとなる。
   */
  ifStateIsNotBlank = (path:string):(event:UIEvent<Event>) => boolean => {
    return event => {
      const model = this.model
      return !!ViewModel.get(path, model)
    }
  }
  /**
   * 【画面モデル項目:path:string】のリスト内容が空であれば
   *
   * 指定されたパス上の表示モデルのリストが、0件の場合trueとなる。
   */
  ifStateIsListEmpty = (path:string):(event:UIEvent<Event>) => boolean => {
    return event => {
      const model = this.model
      return !ViewModel.get(path, model) || ViewModel.get(path, model).length == 0
    }
  }
  /**
   * 【画面モデル項目:path:string】のリスト内容が空でなければ
   *
   * 指定されたパス上の表示モデルのリストが、1件以上の場合trueとなる。
   */
  ifStateIsNotListEmpty = (path:string):(event:UIEvent<Event>) => boolean => {
    return event => {
      const model = this.model
      return !!ViewModel.get(path, model) && ViewModel.get(path, model).length > 0
    }
  }
  /**
   * 【画面モデル項目:path:string】の【画面モデル項目:checkflagPath:string】のチェック状態が0件であれば
   *
   * 指定されたパス上の表示モデルのリスト内容に、指定されたチェックフラグのチェック状態が0件ならtrueとなる。
   */
  ifStateTableColumnNotChecked = (path:string, checkflagPath:string):(event:UIEvent<Event>) => boolean => {
    return event => {
      const model = this.model
      const records = JSON.parse(JSON.stringify(ViewModel.get(path, model))) || []
      return records.filter(record => !!record[checkflagPath.split('.').slice(-1)[0]]).length == 0
    }
  }
  /**
   * 【画面モデル項目:path:string】の【画面モデル項目:checkflagPath:string】のチェック状態が1件以上であれば
   *
   * 指定されたパス上の表示モデルのリスト内容に、指定されたチェックフラグのチェック状態が1件以上ならtrueとなる。
   */
  ifStateTableColumnChecked = (path:string, checkflagPath:string):(event:UIEvent<Event>) => boolean => {
    return event => {
      const model = this.model
      const records = JSON.parse(JSON.stringify(ViewModel.get(path, model))) || []
      return records.filter(record => !!record[checkflagPath.split('.').slice(-1)[0]]).length > 0
    }
  }
  /**
   * 【画面モデル項目:path:string】の【画面モデル項目:checkflagPath:string】のチェック状態が【値:count:(m:ViewModel) => number】件以上であれば
   *
   * 指定されたパス上の表示モデルのリスト内容に、指定されたチェックフラグのチェック状態が指定した件数以上ならtrueとなる。
   */
  ifStateTableColumnMultiChecked = (path:string, checkflagPath:string, count:(m:ViewModel) => number):(event:UIEvent<Event>) => boolean => {
    return event => {
      const model = this.model
      const records = JSON.parse(JSON.stringify(ViewModel.get(path, model))) || []
      return records.filter(record => !!record[checkflagPath.split('.').slice(-1)[0]]).length >= count(model)
    }
  }
  /**
   * 値【値:value:(m:ViewModel) => any】が【値:valueAnother:(m:ViewModel) => any】であれば
   *
   * 指定された2つの値が一致するならtrueとなる。
   */
  ifValueIsSameWith = (value:(m:ViewModel) => any, valueAnother:(m:ViewModel) => any):(event:UIEvent<Event>) => boolean => {
    return event => {
      const model = this.model
      return value(model) == valueAnother(model)
    }
  }
  /**
   * 値【値:value:(m:ViewModel) => any】が【値:valueAnother:(m:ViewModel) => any】でなければ
   *
   * 指定された2つの値が一致しないならtrueとなる。
   */
  ifValueIsNotSameWith = (value:(m:ViewModel) => any, valueAnother:(m:ViewModel) => any):(event:UIEvent<Event>) => boolean => {
    return event => {
      const model = this.model
      return value(model) !== valueAnother(model)
    }
  }
  /**
   * ログイン状態であれば
   *
   * AUTH_TICKETがブラウザに保存されていればtrueとなる。（保存先: 本番->cookie, 本番以外->localstorage）
   */
  ifStateHasAlreadyLoggedIn = ():(event:UIEvent<Event>) => boolean => {
    return event => {
      const model = this.model
      return this.hasAuthTicket
    }
  }
}


// WEBPACK FOOTER //
// ./src/services/event.service.ts